import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import autoComplete from '@tarekraafat/autocomplete.js';

import Component from '../component/component';

import AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;
import MapMouseEvent = google.maps.MapMouseEvent;

import './map.scss';

interface IMarkerData {
    lat: string;
    lng: string;
    title: string;
}

export interface IMapSettings {
    lat: string;
    lng: string;
    markers: IMarkerData[];
}

const SELECTED_Z_INDEX: number = 9999999;

export default class Map extends Component {
    static initSelector: string = '.map';
    public timeout: number;
    public map: HTMLElement;
    public markers: Record<string, AdvancedMarkerElement>;
    public settings: IMapSettings;
    public googleMap: google.maps.Map;
    public latLng: google.maps.LatLng;
    public overlays: google.maps.OverlayView[];
    public styles: google.maps.MapTypeStyle[];
    public markerElements: HTMLElement[];
    public cluster: MarkerClusterer;
    public autoCompleteStrings: string[] = [];
    public inited: boolean = false;

    constructor(target: HTMLElement) {
        super(target);
        this.element = $(target).find('.map__container');

        this.setupCheckboxEventListeners();

        this.settings = {
            lat: this.element.data('lat'),
            lng: this.element.data('lng'),
            markers: this.element.find('.map__tag').map((index: number, markerElement: HTMLElement) => {
                const markerData: JQuery.PlainObject = $(markerElement).data();

                return {
                    lat: markerData.lat,
                    lng: markerData.lng,
                    link: markerData.link,
                    title: markerData.title,
                } as IMarkerData;
            }).get(),
        };

        this.markerElements = Array.from(this.element[0].querySelectorAll('.map__tag'));

        if (window.gotoAndPlay.googleMapsApiKey) {
            const loader: Loader = new Loader({
                apiKey: window.gotoAndPlay.googleMapsApiKey,
                libraries: ['maps','geometry'],
            });

            loader.load().then(async (): Promise<void> => {
                const userLatLng: google.maps.LatLng = await this.getUserLocation();

                if (userLatLng) {
                    this.latLng = userLatLng;
                } else {
                    this.latLng = new google.maps.LatLng(Number(this.settings.lat), Number(this.settings.lng));
                }

                await google.maps.importLibrary('maps');
                await google.maps.importLibrary(
                    'marker',
                );
                this.initMap();

                if (this.markerElements.length > 0) {
                    this.setUpAutoComplete();

                    const clearSearch: JQuery = this.element.parents(Map.initSelector).find('.textfield__clear');

                    clearSearch.insertAfter(this.element.parents(Map.initSelector).find('.map__search input'));
                }
            });

        } else {
            console.error('No Google Maps API key defined in gotoAndPlay');
        }
    }

    // eslint-disable-next-line
    async setUpAutoComplete(): Promise<void> {

        this.markerElements.forEach((element: HTMLElement): void => {
            const tag: JQuery = $(element);
            const service: string = tag.data('service');
            const title: string = tag.attr('data-title')?.trim() || '';
            const address: string = tag.attr('data-address')?.trim() || '';
            const id: string = tag.find('.map-tag').data('id');
            const imageData: string = tag.find('.map-tag').data('image'); // Retrieve the image data

            this.autoCompleteStrings.push(`
            <div data-id="${id}" data-lng="${tag.data('lng')}" data-lat="${tag.data('lat')}" data-title="${title}" data-address="${address}" data-service="${service}" class="map__result-list-item-inner">
                <img src="${imageData}" alt="${title}" class="map__result-list-item-icon" width="24" height="24" />
                <span class="map__result-list-item-title">
                    ${title}
                    <div class="map__result-list-item-address">${address}</div>
                </span>
                <span class="map__result-list-item-arrow-container">
                    <span class="map__result-list-item-arrow">→</span>
                </span>
                <span class="h-hidden">${address}</span>
            </div>
        `);
        });

        // Sort autoCompleteStrings based on distance from user's location
        this.autoCompleteStrings.sort((a: string, b: string): number => {
            // eslint-disable-next-line
            const parseLatLng: any = (input: string, coord: 'lat' | 'lng'): number => {
                const match: RegExpMatchArray = input.match(new RegExp(`data-${coord}="([^"]+)"`));

                return match ? parseFloat(match[1]) : 0;
            };

            const aLatLng: google.maps.LatLng = new google.maps.LatLng(parseLatLng(a, 'lat'), parseLatLng(a, 'lng'));
            const bLatLng: google.maps.LatLng = new google.maps.LatLng(parseLatLng(b, 'lat'), parseLatLng(b, 'lng'));

            const aDistance: number = this.latLng ? google.maps.geometry.spherical.computeDistanceBetween(this.latLng, aLatLng) : 0;
            const bDistance: number = this.latLng ? google.maps.geometry.spherical.computeDistanceBetween(this.latLng, bLatLng) : 0;

            return aDistance - bDistance;
        });

        // eslint-disable-next-line
        const autoCompleteJS: any = new autoComplete(
            {
                selector: '#map-search-input',
                data: {
                    src: this.autoCompleteStrings,
                    cache: true,
                },
                threshold: 2,
                resultsList: {
                    // eslint-disable-next-line
                    element: (list: any, data: any): void => {
                        if (!data.results.length) {
                            const message: HTMLDivElement = document.createElement('div');

                            message.setAttribute('class', 'map__no-results');
                            message.innerHTML = `<span>Found No Results for "${data.query}"</span>`;

                            list.prepend(message);
                        }
                    },
                    class: 'map__result-list',
                    noResults: true,
                },
                resultItem: {
                    highlight: false,
                    class: 'map__result-list-item',
                    selected: 'is-selected',
                    // eslint-disable-next-line
                    element: (item: HTMLElement, data: any): void => {
                        item.setAttribute('data-id', $(item).find('.map__result-list-item-inner').data('id'));
                    },
                },
                events: {
                    input: {
                        // eslint-disable-next-line
                        selection: async (event: any): Promise<void> => {
                            // eslint-disable-next-line
                            const selection: JQuery<any> = $(event.detail.selection.value);
                            const id: string = selection.data('id');

                            const marker: AdvancedMarkerElement = this.markers[id];

                            this.googleMap.setZoom(18);

                            this.googleMap.panTo(this.calculateOffsetPosition(
                                selection,
                                typeof marker.position.lat === 'function' ? marker.position.lat().toString() : marker.position.lat.toString(),
                                typeof marker.position.lng === 'function' ? marker.position.lng().toString() : marker.position.lng.toString(),
                            ));

                            new google.maps.event.trigger(marker, 'click');
                        },
                    },
                },
            },
        );
    }

    initMap(): void {
        this.googleMap = new google.maps.Map(this.element[0], {
            center: this.latLng,
            zoom: 8,
            scrollwheel: false,
            styles: this.styles,
            mapId: 'iuteMap',
            mapTypeControl: false,
        });

        this.googleMap.addListener('tilesloaded', (): void => {
            this.initMarkers();
        });

        $('body').on('click', '.map-tag__close-btn', (event: JQuery.ClickEvent): void => {
            event.preventDefault();
            event.stopImmediatePropagation();
            $('.map-tag__info-container').addClass('h-invisible');
        });

        this.googleMap.addListener('zoom_changed', (): void => {
            $('.map-tag__info-container').addClass('h-invisible');
        });
    }

    initMarkers(): void {
        if (!this.inited) {
            this.inited = true;
            this.markers = {};

            const markers: AdvancedMarkerElement[] = this.settings.markers.map((markerData: IMarkerData, index: number): AdvancedMarkerElement => {
                const markerElement: HTMLElement = this.markerElements[index];
                const mapTag: JQuery = $(markerElement).find('.map-tag');
                const markerId: string = mapTag.data('id');

                const lat: number = Number(markerData.lat);
                const lng: number = Number(markerData.lng);

                // Check if lat and lng are valid numbers and do not contain a comma
                if (Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && !markerData.lat.toString().includes(',') && !markerData.lng.toString().includes(',')) {
                    const marker: AdvancedMarkerElement = new google.maps.marker.AdvancedMarkerElement({
                        position: {
                            lat,
                            lng,
                        },
                        content: markerElement,
                    });

                    if (marker === undefined) {
                        return undefined;
                    }

                    const data: JQuery.PlainObject = $(markerElement).find('.map-tag').data();

                    for (const key in data) {
                        if (key.startsWith('category') && data[key] !== undefined) {
                            marker.setAttribute(key, '1');
                        }
                    }

                    marker.setAttribute('markerId', markerId);

                    mapTag.find('.map-tag__image').on('click', (): void => {
                        this.onMapClick(markers, marker);
                    });

                    mapTag.find('.map-tag__close-btn').on('click', (event: JQuery.ClickEvent): void => {
                        event.preventDefault();
                        event.stopImmediatePropagation();
                        $('.map-tag__info-container').addClass('h-invisible');
                    });

                    marker.addListener('click', (event: MapMouseEvent): void => {
                        if (!event || $(event.domEvent.target).hasClass('map-tag__link')) {
                            this.onMapClick(markers, marker);
                        }
                    });

                    markerElement.addEventListener('mousedown', (event: MouseEvent): void => {
                        event.stopPropagation();
                    });

                    markerElement.addEventListener('touchstart', (event: TouchEvent): void => {
                        event.stopPropagation();
                    });

                    markerElement.addEventListener('contextmenu', (event: Event): void => {
                        event.stopPropagation();
                    });

                    this.markers[markerId] = marker;

                    mapTag.data('marker', marker);

                    return marker;
                }

                return undefined;
            });

            const validMarkers: AdvancedMarkerElement[] = markers.filter((marker: AdvancedMarkerElement | undefined): marker is AdvancedMarkerElement => marker !== undefined);

            this.cluster = new MarkerClusterer({ markers: validMarkers, map: this.googleMap });
            this.checkFilterStatus();
        }
    }

    private onMapClick(markers: AdvancedMarkerElement[], marker: AdvancedMarkerElement): void {
        markers.forEach((m: AdvancedMarkerElement | undefined): void => {
            if (m !== undefined) {
                m.zIndex = 1;
            }
        });

        const markerId: string = marker.getAttribute('markerid');

        $('.map-tag__info-container').addClass('h-invisible');

        $('#marker-popup-' + markerId).appendTo($(marker.element).find('.map-tag__link'));

        $(marker.element).find('.map-tag__info-container').removeClass('h-invisible');

        setTimeout(() => {
            this.googleMap.panTo(this.calculateOffsetPosition(
                $(marker.element).find('.map-tag__info-container'),
                typeof marker.position.lat === 'function' ? marker.position.lat().toString() : marker.position.lat.toString(),
                typeof marker.position.lng === 'function' ? marker.position.lng().toString() : marker.position.lng.toString(),
            ));
        }, 50);

        marker.zIndex = SELECTED_Z_INDEX;
    }

    private async getUserLocation(): Promise<google.maps.LatLng | null> {
        // eslint-disable-next-line
        return new Promise((resolve): void => {
            // eslint-disable-next-line
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position: GeolocationPosition): void => {
                        const userLatLng: google.maps.LatLng = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);

                        resolve(userLatLng);
                    },
                    (error: GeolocationPositionError): void => {
                        console.error('Error getting user location:', error.message);
                        resolve(null);
                    },
                );
            } else {
                console.error('Geolocation not supported');
                resolve(null);
            }
        });
    }

    private calculateOffsetPosition(target: JQuery, latStr: string, lngStr: string): google.maps.LatLng {

        const lat: number = parseFloat(latStr);
        const lng: number = parseFloat(lngStr);
        const offsetX: number = -target.width() / 2;
        const offsetY: number = target.height() / 2;

        return this.calculateOffset(offsetX, offsetY, lat, lng);
    }

    private calculateOffset(offsetX: number, offsetY: number, lat: number, lng: number): google.maps.LatLng {
        const scale: number = Math.pow(2, this.googleMap.getZoom());

        const worldCoordinateCenter: google.maps.Point = this.googleMap.getProjection().fromLatLngToPoint(new google.maps.LatLng(lat, lng));
        const pixelOffset: google.maps.Point = new google.maps.Point((offsetX / scale) || 0, (offsetY / scale) || 0);

        const worldCoordinateNewCenter: google.maps.Point = new google.maps.Point(
            worldCoordinateCenter.x - pixelOffset.x,
            worldCoordinateCenter.y + pixelOffset.y,
        );

        return this.googleMap.getProjection().fromPointToLatLng(worldCoordinateNewCenter);
    }

    private setupCheckboxEventListeners(): void {
        const checkboxes: NodeListOf<Element> = document.querySelectorAll('input[type="checkbox"]');

        checkboxes.forEach((checkbox: Element): void => {
            checkbox.addEventListener('change', (): void => {
                this.checkFilterStatus();
            });
        });
    }

    private checkFilterStatus(): void {
        const parent: JQuery = this.element.parents(Map.initSelector);
        const checkedCategories: JQuery = parent.find('.checkbox--map input[type="checkbox"]:checked');
        const checkCategoryIds: string[] = [];

        checkedCategories.each((index: number, element: HTMLElement): void => {
            checkCategoryIds.push(element.getAttribute('data-category-id'));
        });

        Object.values(this.markers).forEach((marker: AdvancedMarkerElement): void => {
            let isVisible: boolean = false;

            checkCategoryIds.forEach((categoryId: string): void => {
                if (marker.getAttribute('category-'+categoryId)) {
                    isVisible = true;
                }
            });

            if (isVisible) {
                this.cluster.addMarker(marker);
            } else {
                this.cluster.removeMarker(marker);
            }
        });
    }
}
